import { RetryPresentation, RetryPresentationState } from '@/types/stepExecutions.ts';
import { ExecutionStatus } from '@/types/statuses.ts';
import { getRetryAttemptOutcomeTone } from '@/pages/Run/components/retryAttemptPresentation.ts';
import type { RetryAttemptOutcomeTone } from '@/pages/Run/components/retryAttemptPresentation.ts';

export type RetryAttemptItem = {
  value: string;
  label: string;
  status: ExecutionStatus | null;
  childIndex: number;
  attemptNumber?: number;
  parentPath?: string;
  scopeParentStepRunId?: string;
  outcomeLabel?: string;
  outcomeTone?: RetryAttemptOutcomeTone;
  detailText?: string;
  lifecycleText?: string;
  historical?: boolean;
};

type UseRetryAttemptItemsArgs = {
  presentation?: RetryPresentation | null;
};

export const buildRetryAttemptItems = ({
  presentation,
}: UseRetryAttemptItemsArgs): {
  items: RetryAttemptItem[];
  latestItemValue: string | null;
} => {
  if (!presentation) return { items: [], latestItemValue: null };

  const toItem = (attempt: RetryPresentation['current_attempt'], historical: boolean): RetryAttemptItem => ({
    value: attempt.attempt_number.toString(),
    label: `Attempt ${attempt.ordinal}`,
    status: getRetryAttemptStatus(attempt.state),
    childIndex: attempt.scope.child_index,
    attemptNumber: attempt.scope.attempt_number,
    parentPath: attempt.scope.parent_path,
    scopeParentStepRunId: attempt.scope.scope_parent_step_run_id,
    outcomeLabel: attempt.title,
    outcomeTone: getRetryAttemptOutcomeTone(attempt.state),
    detailText: [
      attempt.detail,
      ...(attempt.submission_evidence === 'attempted'
        ? ['A submission was attempted.']
        : attempt.submission_evidence === 'not_attempted'
          ? ['No submission was attempted.']
          : []),
    ]
      .filter(Boolean)
      .join(' ') || undefined,
    historical,
  });

  return {
    items: [
      ...presentation.previous_attempts.map((attempt) => toItem(attempt, true)),
      toItem(presentation.current_attempt, false),
    ],
    latestItemValue: presentation.current_attempt.attempt_number.toString(),
  };
};

const getRetryAttemptStatus = (state: RetryPresentationState): ExecutionStatus => {
  switch (state) {
    case 'succeeded':
      return ExecutionStatus.Completed;
    case 'queued':
      return ExecutionStatus.Pending;
    case 'running':
      return ExecutionStatus.Active;
    case 'needs_review':
    case 'automatic_retry_withheld':
      return ExecutionStatus.AskUser;
    case 'automatically_retried':
    case 'stopped':
    case 'retry_limit_reached':
      return ExecutionStatus.Errors;
  }
};
